import { useStore } from '@assets/model/Store';
import { observer } from 'mobx-react';
import { DataAccessUtils } from '../../model/DataAccessUtils';
import { DealScore } from '../DealDetails/DealScore/DealScore';
import {
    formatDollar,
    formatPercent,
    formatPercentNoMultiply,
    noFormat,
    noFormatRaw
} from '../Financial/PanelCompanyYearly/PanelElements/Formatters';
import { RowLabel } from '../Financial/PanelCompanyYearly/PanelElements/RowLable/RowLable';
import { RowValue } from '../Financial/PanelCompanyYearly/PanelElements/RowValue/RowValue';
import {
    PanelColumn,
    PanelRow,
    PanelTable
} from '../Panel/PanelTable/PanelTable';

import { CompanyCompareUtils } from '@assets/model/CompanyCompareUtils';
import { DataAccessAggregateUtils } from '@assets/model/DataAccessAggregateUtils';
import {
    ExtendedFieldsSchema,
    ExtendedSchemaField
} from '@assets/model/FieldsSchema';
import {
    getRules,
    RuleType,
    ScoreFieldsGroup,
    ScoreUtils,
    useScoreStore
} from '@assets/model/ScoreUtils';
import { useNavUtils } from '@assets/utils/NavUtils';
import classNames from 'classnames';
import { assert } from 'console';
import { ImageUI } from '../Image/Image';
import { PanelTitle } from '../Panel/PanelTitle/PanelTitle';
import { ScrollPanel } from '../Panel/ScrollPanel/ScrollPanel';
import { SectionIcon } from '../SectionIcons/SectionIcon';
import { Flex } from '../UI/Flex/Flex';
import * as style from './DealCompare.module.less';
import industryIcon from './industry_icon.svg';
import { formatPercentStr } from '@assets/utils/Formatting';
import { isString } from 'lodash';
import { Fragment } from 'react';
export const DealCompare = observer(() => {
    const store = useStore();
    const navUtils = useNavUtils();
    const INDUSTRY_AVG_ID = 'INDUSTRY_AVERAGE';
    const SECTIONS_SHOW_NOT_SCORED = ['overview'];

    if (store.selectedReportId == '' || store.query == null) return null;
    const q = store.query;

    const companies = [
        store.selectedReportId,
        ...CompanyCompareUtils.findCompaniesToCompare(store.selectedReportId),
        INDUSTRY_AVG_ID
    ];

    const dataAccess = { string: DataAccessUtils };
    const aggAccess = new DataAccessAggregateUtils(
        q.value('industry'),
        q.value('industrySubType')
    );
    companies.forEach(company => {
        if (company == INDUSTRY_AVG_ID) {
            return;
        }
        const query = store.createDataAccess(company);
        dataAccess[company] = query;
    });

    const getElement = (fieldName: string, rule: RuleType) => {
        const cagrPercentElement = RowValue.custom(
            (row: PanelRow, col: PanelColumn, data: any) => {
                type cagrYearly = { [key: number]: number };
                var typed: cagrYearly = data as cagrYearly;
                var keys = Object.keys(typed).sort();
                var children = [];
                for (let i = 0; i < keys.length; i++) {
                    const yearIndex = keys[i];
                    const value = typed[yearIndex];
                    if (value != null) {
                        var valDisplay =
                            value == 'N/A'
                                ? 'N/A'
                                : formatPercentStr(value.toString());
                        children.push(
                            <Fragment key={yearIndex}>
                                <div>{valDisplay}</div>
                                <div key={yearIndex}>({yearIndex} years) </div>
                                <div className="w-100" />
                            </Fragment>
                        );
                    }
                }
                return (
                    <Flex
                        direction="row"
                        justify="center"
                        fillCell={true}
                        wrap="wrap"
                        style={{ gap: '5px' }}
                    >
                        {children}
                    </Flex>
                );
            }
        );

        const e: ExtendedSchemaField = ExtendedFieldsSchema[fieldName];
        const rules = getRules();

        if (e.t == 'bool') {
            return RowValue.booleanCentered;
        } else if (e.t == '%') {
            if (rule.dataPick != null) {
                return cagrPercentElement;
            }
            return RowValue.percent;
        } else {
            return RowValue.value;
        }
    };

    const getLabel = (fieldName: string) => {
        const e: ExtendedSchemaField = ExtendedFieldsSchema[fieldName];

        if (e.t == '%') {
            return RowLabel.percent_label;
        } else {
            return RowLabel.label;
        }
    };

    const formatPercentWithYear = (data: any) => {
        if (!data && data !== 0) {
            data = '';
        }

        if (isString(data)) {
            return data;
        }

        interface valueFormat {
            years: number;
            value: number;
        }
        const v = data as valueFormat;
        return `${formatPercentStr(v.value.toString())} (${v.years} years)`;
    };

    const getFormatter = (fieldName: string, rule: RuleType) => {
        const e: ExtendedSchemaField = ExtendedFieldsSchema[fieldName];

        if (e.t == '%') {
            if (rule.dataPick != null) {
                return noFormatRaw;
            }
            if (e.scale == 100) {
                return formatPercentNoMultiply;
            }

            return formatPercent;
        } else if (e.t == 'bool' || e.t == 'str') {
            return null;
        } else {
            return formatDollar;
        }
    };
    var scoreStore = useScoreStore();
    const fieldsGroup = scoreStore.COMPARE_FIELDS_GROUPS;
    const genColumn = (reportId: string, disableHeaderLogo?: boolean) => {
        return {
            column: {
                source: reportId,
                display: null
            },
            header: (col: PanelColumn) => {
                if (disableHeaderLogo) {
                    return null;
                }

                const q = getQ(col.column.source);
                const name = q.value('company_name');
                var logoURL = null;
                if (col.column.source == INDUSTRY_AVG_ID) {
                    logoURL = industryIcon;
                } else {
                    logoURL = q.value('logoURL');
                }

                return (
                    <a
                        href={`${reportId}`}
                        style={{ textDecoration: 'none', cursor: 'pointer' }}
                        className="companyNameLabel"
                        onClick={e => {
                            e.preventDefault();
                            if (reportId == INDUSTRY_AVG_ID) {
                                return;
                            }

                            navUtils.toReport(reportId);
                        }}
                    >
                        <Flex
                            className={style.companyHeaderElement}
                            direction="column"
                            alignItems="center"
                            justify="between"
                        >
                            <DealScore
                                score={q.score()}
                                className={style.dealThumbScore}
                            />

                            <div className={style.companyNameLabel}>{name}</div>
                            {logoURL && (
                                <ImageUI
                                    src={logoURL}
                                    className={style.companyLogo}
                                />
                            )}
                        </Flex>
                    </a>
                );
            }
        };
    };
    const columns: PanelColumn[] = companies.map(reportId => {
        return genColumn(reportId);
    });

    const getQ = (reportId: string) => {
        if (reportId == INDUSTRY_AVG_ID) {
            return aggAccess;
        }

        return dataAccess[reportId] as DataAccessUtils;
    };

    const fieldsToRows = (group: ScoreFieldsGroup) => {
        const fields = group.fields;
        const rules = getRules();
        return fields.map(fieldName => {
            const e: ExtendedSchemaField = ExtendedFieldsSchema[fieldName];
            const rule = ScoreUtils.get_score_rule_by_fieldname(
                group.sectionId,
                fieldName
            );
            assert(
                e != null,
                `Field ${fieldName} not found in fieldsNameLookup`
            );
            return {
                field: {
                    source: null,
                    display: e.display
                },
                value: (_field, column) => {
                    // if (
                    //     fieldName ==
                    //     'projected_organic_cagr_3_years_minus_tam_growth'
                    // ) {
                    //     debugger;
                    // }
                    const q = getQ(column.source);
                    if (e.t == 'str') {
                        if (fieldName.indexOf('extended_') == 0) {
                            return q.get_extended_value_float(fieldName);
                        }

                        return q.value(fieldName);
                    }

                    return q.get_extended_value_float(fieldName);
                },

                label: getLabel(fieldName),
                formatter: getFormatter(fieldName, rule),
                element: getElement(fieldName, rule)
            };
        });
    };

    const groupTables = fieldsGroup.map((group, i) => {
        const sectionScoreRow = {
            field: {
                source: null,
                display: ''
            },
            value: (_field, column) => {
                const q = getQ(column.source);
                var ret = q.scoreBySection(group.sectionId as any);
                if (ret == -1) {
                    return '-';
                }

                return ret;
            },
            formatter: formatPercent,
            label: RowLabel.percent_label,
            element: (row: PanelRow, col: PanelColumn, data: any) => {
                const score = data == '-' ? -1 : data;
                return (
                    <Flex fillCell={true} alignItems="center" justify="center">
                        <DealScore score={score} />
                    </Flex>
                );
            }
        };
        const groupRows = [...fieldsToRows(group)];

        var shouldShow = companies.some(reportId => {
            if (reportId == INDUSTRY_AVG_ID) {
                return false;
            }

            if (SECTIONS_SHOW_NOT_SCORED.includes(group.sectionId)) {
                return true;
            }

            var scoreUtils = new ScoreUtils(getQ(reportId) as DataAccessUtils);
            if (scoreUtils.is_section_used_for_scoring_by_id(group.sectionId)) {
                return true;
            }

            return false;
        });

        const titlePanel = (
            <PanelTitle
                key={'title_' + group.sectionId}
                title={ScoreUtils.get_section_name(group.sectionId)}
                className={style.panelTitleText}
                containerClassName={style.panelTitle}
            >
                <SectionIcon
                    section={group.sectionId}
                    className={style.sectionIcon}
                />
            </PanelTitle>
        );
        if (!shouldShow) {
            return (
                <div key={'group_nodata_' + i}>
                    {titlePanel}
                    <Flex justify="center">
                        <div className={style.noDataAvail}>
                            No Data Available
                        </div>
                    </Flex>
                </div>
            );
        }
        return (
            <div key={'group_report_' + i}>
                {titlePanel}
                {!SECTIONS_SHOW_NOT_SCORED.includes(group.sectionId) && (
                    <div className={style.panelTableStyleRelativeContainer}>
                        <PanelTable
                            columns={columns}
                            rows={[sectionScoreRow]}
                            disable_round_edges={true}
                            className={style.panelTableStyleScoreLine}
                            noHeaderRow={true}
                        />
                    </div>
                )}
                <PanelTable
                    columns={columns}
                    rows={groupRows}
                    disable_round_edges={true}
                    className={style.panelTableStyle}
                    noHeaderRow={true}
                />
            </div>
        );
    });

    const headersTableStyle = classNames(
        style.panelTableStyle,
        style.panelTableCompanies
    );

    return (
        <>
            <Flex direction="column" fillCell={true}>
                <PanelTable
                    columns={columns}
                    rows={[]}
                    disable_round_edges={true}
                    className={headersTableStyle}
                />
                <ScrollPanel>
                    {groupTables}
                    <div style={{ width: '100%', height: '100px' }}></div>
                </ScrollPanel>
            </Flex>
        </>
    );
});
